// © 2026 AttainIP Engineering
import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import {
  fetchEnterprise,
  fetchEnterpriseUsers,
  fetchEnterpriseMagicLink,
  fetchEnterpriseWorkspace,
  fetchEnterpriseWorkflows,
} from '@/api/queries';
import { api } from '@/api/client';
import { Card } from '@/components/Card';
import { GuestsPanel } from '@/components/GuestsPanel';
import { getCurrentUser, isPlatformAdmin } from '@/lib/auth';

interface Entitlement {
  org_id: string;
  committed_monthly_volume: number;
  balance: number;
  enforcement: 'enforced' | 'advisory';
  topup_cadence: string;
  cycle_started_at: string | null;
  next_topup_at: string | null;
  term_start_at: string | null;
  term_end_at: string | null;
}

interface EnterpriseUser {
  user_id: string;
  email: string;
  display_name: string;
  is_org_admin: boolean;
  status: string;
  roles: string[];
  // RFC-0510 W40. `account_type` is NOT here on purpose: a guest's user row
  // says "enterprise" in the host firm, deliberately, so it can never tell a
  // guest from an employee. `membership_kind` is the fact — 'member', 'guest',
  // or null when Identity has no membership row for them (the ordinary state
  // for an employee provisioned after the 1.8.0 backfill).
  membership_kind?: 'member' | 'guest' | null;
  is_guest?: boolean;
  usage?: { credits_used: number; analyses_count: number };
}

function dateLabel(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

// Next top-up = server-computed next_topup_at, falling back to cycle_started_at
// + 1 month if an older payload omits it.
function nextTopupLabel(entitlement: Entitlement): string {
  if (entitlement.next_topup_at) return dateLabel(entitlement.next_topup_at);
  if (!entitlement.cycle_started_at) return '—';
  const next = new Date(entitlement.cycle_started_at);
  next.setUTCMonth(next.getUTCMonth() + 1);
  return next.toLocaleDateString();
}

// An ISO timestamp -> the YYYY-MM-DD an <input type="date"> expects (UTC day).
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

function EntitlementPanel({
  orgId,
  entitlement,
  canEdit,
}: {
  orgId: string;
  entitlement: Entitlement | null;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [volume, setVolume] = useState(String(entitlement?.committed_monthly_volume ?? 0));
  const [enforcement, setEnforcement] = useState<'enforced' | 'advisory'>(
    entitlement?.enforcement ?? 'advisory',
  );
  const [termEnd, setTermEnd] = useState(toDateInput(entitlement?.term_end_at ?? null));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const startEdit = () => {
    setVolume(String(entitlement?.committed_monthly_volume ?? 0));
    setEnforcement(entitlement?.enforcement ?? 'advisory');
    setTermEnd(toDateInput(entitlement?.term_end_at ?? null));
    setSaveError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    const amount = parseInt(volume, 10);
    if (isNaN(amount) || amount < 0) return;
    if (!window.confirm(`Set committed monthly volume to ${amount} credits (${enforcement})?`)) return;

    // Only send term_end_at when the operator provided one; an empty field
    // leaves the firm's existing term untouched (RFC-0501).
    const payload: {
      committed_monthly_volume: number;
      enforcement: 'enforced' | 'advisory';
      term_end_at?: string;
    } = { committed_monthly_volume: amount, enforcement };
    if (termEnd) payload.term_end_at = `${termEnd}T00:00:00Z`;

    setSaving(true);
    setSaveError(null);
    try {
      await api.put(`/v1/enterprises/${orgId}/entitlement`, payload);
      queryClient.invalidateQueries({ queryKey: ['enterprise', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enterprise-entitlement', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enterprises'] });
      setEditing(false);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const message = detail?.message ?? (typeof detail === 'string' ? detail : null);
      setSaveError(message || 'Failed to update entitlement');
    } finally {
      setSaving(false);
    }
  };

  if (!entitlement && !editing) {
    return (
      <Card title="Entitlement">
        <p className="text-sm text-gray-400 mb-3">No credit pool configured for this firm yet.</p>
        {canEdit && (
          <button
            onClick={startEdit}
            className="text-xs px-3 py-1.5 bg-gray-900 text-white rounded hover:bg-gray-800"
          >
            Configure entitlement
          </button>
        )}
      </Card>
    );
  }

  return (
    <Card title="Entitlement">
      {editing ? (
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Committed monthly volume</label>
            <input
              type="number"
              min={0}
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Enforcement</label>
            <select
              value={enforcement}
              onChange={(e) => setEnforcement(e.target.value as 'enforced' | 'advisory')}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="advisory">advisory</option>
              <option value="enforced">enforced</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Term expiry</label>
            <input
              type="date"
              aria-label="Term expiry"
              value={termEnd}
              onChange={(e) => setTermEnd(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Renew/extend the term. Changing this only moves when the balance expires —
              it never grants or reclaims credits.
            </p>
          </div>
          {saveError && <p className="text-xs text-red-600">{saveError}</p>}
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-xs px-3 py-1.5 bg-gray-900 text-white rounded hover:bg-gray-800 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setEditing(false)}
              disabled={saving}
              className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="space-y-2 text-sm">
          <div className="flex">
            <dt className="w-40 text-gray-500">Committed monthly volume</dt>
            <dd className="font-medium">{entitlement!.committed_monthly_volume}</dd>
          </div>
          <div className="flex">
            <dt className="w-40 text-gray-500">Balance (accrued)</dt>
            <dd className="font-medium">{entitlement!.balance}</dd>
          </div>
          <div className="flex">
            <dt className="w-40 text-gray-500">Enforcement</dt>
            <dd>
              <span
                className={`text-xs px-2 py-0.5 rounded ${
                  entitlement!.enforcement === 'enforced'
                    ? 'bg-red-50 text-red-600'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {entitlement!.enforcement}
              </span>
            </dd>
          </div>
          <div className="flex">
            <dt className="w-40 text-gray-500">Next credit top-up</dt>
            <dd className="text-xs">{nextTopupLabel(entitlement!)}</dd>
          </div>
          <div className="flex">
            <dt className="w-40 text-gray-500">Term expiry</dt>
            <dd className="text-xs">{dateLabel(entitlement!.term_end_at)}</dd>
          </div>
          {canEdit ? (
            <button
              onClick={startEdit}
              className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 mt-1"
            >
              Edit entitlement
            </button>
          ) : (
            <p className="text-xs text-gray-400 mt-1">Read-only — only platform_admin can edit the entitlement.</p>
          )}
        </dl>
      )}
    </Card>
  );
}

/**
 * The org_admin control for one firm user (RFC-0211 A2 / RFC-0510 W40).
 *
 * ⚠️ A GUEST IS NEVER OFFERED "Set admin". org_admin administers the firm, and
 * a guest belongs to another one — they are only granted access to act inside
 * this one. Identity refuses the grant outright (`guest_target_forbidden`);
 * this is the second line of defence, not the first. The reason it is worth
 * having anyway is the same reason the server check is worth having: the grant
 * used to SUCCEED, and the console then displayed a guest as an admin of a firm
 * they do not belong to, which support reads as truth.
 *
 * "Unset admin" IS still offered on a guest that carries the role — those rows
 * are exactly the residue of the defect, and hiding the control would leave the
 * only remedy a hand-written UPDATE. Identity permits the revoke for the same
 * reason.
 */
function UserOrgAdminToggle({
  orgId,
  user,
}: {
  orgId: string;
  user: EnterpriseUser;
}) {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  // Positive evidence only: an unknown membership kind is not a guest.
  const isGuest = user.is_guest === true || user.membership_kind === 'guest';

  const handleToggle = async () => {
    setLoading(true);
    try {
      await api.patch(`/v1/enterprises/${orgId}/users/${user.user_id}/org-admin`, {
        granted: !user.is_org_admin,
      });
      queryClient.invalidateQueries({ queryKey: ['enterprise-users', orgId] });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const message = detail?.message ?? (typeof detail === 'string' ? detail : null);
      alert(message || 'Failed to update org_admin role');
    } finally {
      setLoading(false);
    }
  };

  if (isGuest && !user.is_org_admin) {
    return (
      <span className="text-xs text-gray-400" title="A guest of this firm cannot administer it">
        Not available for guests
      </span>
    );
  }

  return (
    <>
      <button
        onClick={handleToggle}
        disabled={loading}
        className={`text-xs px-2 py-0.5 rounded border disabled:opacity-50 ${
          user.is_org_admin
            ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
            : 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100'
        }`}
      >
        {loading ? '...' : user.is_org_admin ? 'Unset admin' : 'Set admin'}
      </button>
      {isGuest && (
        <span className="block text-xs text-red-600 mt-1">
          A guest should not hold org_admin — revoke it.
        </span>
      )}
    </>
  );
}

function UsersPanel({ orgId }: { orgId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['enterprise-users', orgId],
    queryFn: () => fetchEnterpriseUsers(orgId),
  });

  const users: EnterpriseUser[] = data?.data || [];

  return (
    <Card title={`Users (${users.length})`}>
      {isLoading && <p className="text-sm text-gray-400">Loading users...</p>}
      {error && <p className="text-sm text-red-600">Failed to load users</p>}
      {!isLoading && !error && users.length === 0 && (
        <p className="text-sm text-gray-400">No users in this firm</p>
      )}
      {!isLoading && !error && users.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 font-medium">Name</th>
              <th className="pb-2 font-medium">Email</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 font-medium text-right">Credits used</th>
              <th className="pb-2 font-medium text-right">Analyses</th>
              <th className="pb-2 font-medium">org_admin</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.user_id} className="border-b last:border-0">
                <td className="py-2">
                  {user.display_name || '—'}
                  {(user.is_guest === true || user.membership_kind === 'guest') && (
                    <span
                      className="ml-2 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700"
                      title="Belongs to another organisation; granted access to this one"
                    >
                      Guest
                    </span>
                  )}
                </td>
                <td className="py-2 font-mono text-xs">{user.email}</td>
                <td className="py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {user.status || 'unknown'}
                  </span>
                </td>
                <td className="py-2 text-right font-mono text-xs">{user.usage?.credits_used ?? 0}</td>
                <td className="py-2 text-right font-mono text-xs">{user.usage?.analyses_count ?? 0}</td>
                <td className="py-2">
                  <UserOrgAdminToggle orgId={orgId} user={user} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function LoginPolicyPanel({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['enterprise-magic-link', orgId],
    queryFn: () => fetchEnterpriseMagicLink(orgId),
  });

  const enabled: boolean = !!data?.magic_link_enabled;

  const handleToggle = async () => {
    setSaving(true);
    try {
      await api.put(`/v1/enterprises/${orgId}/magic-link`, { enabled: !enabled });
      queryClient.invalidateQueries({ queryKey: ['enterprise-magic-link', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enterprise', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enterprises'] });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const message = detail?.message ?? (typeof detail === 'string' ? detail : null);
      alert(message || 'Failed to update magic-link policy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Login Policy">
      {isLoading && <p className="text-sm text-gray-400">Loading...</p>}
      {error && <p className="text-sm text-red-600">Failed to load magic-link policy</p>}
      {!isLoading && !error && (
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={enabled}
            disabled={saving}
            onChange={handleToggle}
            className="rounded"
          />
          Magic-link login {enabled ? 'enabled' : 'disabled'} for this firm (default off)
        </label>
      )}
    </Card>
  );
}

/**
 * Per-firm Workspace policy (RFC-0500 A5 / RFC-0206 W14).
 *
 * ⚠️ THREE STATES, NOT A CHECKBOX — unlike the Login Policy panel above.
 * `null` means the firm has no override and follows whatever this environment
 * defaults to; it is NOT the same as "off". A checkbox would collapse the two,
 * showing every unconfigured firm as explicitly denied and leaving no way to
 * hand a firm back to the default once it had been set.
 *
 * The environment default is deliberately NOT shown here: this console talks to
 * many environments' data but renders in one, so displaying "(currently on)"
 * would be a guess. What the operator sets is the override; what the firm ends
 * up seeing is that override resolved against the environment they log into.
 */
function WorkspacePolicyPanel({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['enterprise-workspace', orgId],
    queryFn: () => fetchEnterpriseWorkspace(orgId),
  });

  // `undefined` (still loading / absent) and `null` (explicitly unset) both
  // mean "no override" — normalise once so the radio group has one truth.
  const current: boolean | null = data?.workspace_enabled ?? null;

  const setPolicy = async (value: boolean | null) => {
    setSaving(true);
    try {
      await api.put(`/v1/enterprises/${orgId}/workspace`, { enabled: value });
      queryClient.invalidateQueries({ queryKey: ['enterprise-workspace', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enterprise', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enterprises'] });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const message = detail?.message ?? (typeof detail === 'string' ? detail : null);
      alert(message || 'Failed to update Workspace policy');
    } finally {
      setSaving(false);
    }
  };

  const options: Array<{ value: boolean | null; label: string; hint: string }> = [
    { value: null, label: 'Environment default', hint: 'No override — follows the environment' },
    { value: true, label: 'Enabled', hint: 'This firm has the Workspace' },
    { value: false, label: 'Disabled', hint: 'Withheld even where the environment enables it' },
  ];

  return (
    <Card title="Workspace">
      {isLoading && <p className="text-sm text-gray-400">Loading...</p>}
      {error && <p className="text-sm text-red-600">Failed to load Workspace policy</p>}
      {!isLoading && !error && (
        <div className="space-y-2">
          {options.map((opt) => (
            <label
              key={String(opt.value)}
              className="flex items-start gap-2 cursor-pointer text-sm"
            >
              <input
                type="radio"
                name={`workspace-policy-${orgId}`}
                checked={current === opt.value}
                disabled={saving}
                onChange={() => setPolicy(opt.value)}
                className="mt-1"
              />
              <span>
                {opt.label}
                <span className="block text-xs text-gray-400">{opt.hint}</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * Per-firm ENABLED WORKFLOWS (RFC-0511 B).
 *
 * ⚠️ A SET UNDER A CEILING, NOT A ROW OF SWITCHES. The boxes are exactly the
 * workflows THIS ENVIRONMENT's registry admits (`enabled_in`), served with the
 * firm's value so the two can never disagree. A firm's set may only NARROW
 * that list; the BFF refuses a key the environment does not admit, so this
 * control offers none.
 *
 * ⚠️ THREE STATES AGAIN, AND THE FIRST ONE IS NOT "NONE TICKED".
 *   Environment default  no per-org set (`null`) — the firm gets whatever this
 *                        environment admits. EVERY FIRM IS HERE until an
 *                        operator says otherwise, which is why shipping the
 *                        column changes nobody's behaviour.
 *   Only these           an explicit set, possibly EMPTY. Empty is a decision
 *                        ("none of them"), not an absence, and it is why
 *                        "no boxes ticked" cannot be allowed to mean "unset".
 * Collapsing the two would leave no way to hand a firm back to the default
 * once it had been narrowed — the same mistake `workspace_enabled` avoids one
 * card up.
 *
 * The environment's own answer is deliberately not rendered as "(on)" beside
 * each box: this console talks to many environments' data but is served from
 * one, and `available` already IS this environment's answer.
 */
function WorkflowAccessPanel({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['enterprise-workflows', orgId],
    queryFn: () => fetchEnterpriseWorkflows(orgId),
  });

  // `undefined` (loading/absent) and `null` (explicitly unset) both mean "no
  // per-org set" — normalise once so the radio group has one truth.
  const current: string[] | null = data?.enabled_workflows ?? null;
  const available = data?.available ?? [];
  const overridden = current !== null;

  const save = async (value: string[] | null) => {
    setSaving(true);
    try {
      await api.put(`/v1/enterprises/${orgId}/workflows`, { enabled_workflows: value });
      queryClient.invalidateQueries({ queryKey: ['enterprise-workflows', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enterprise', orgId] });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const message = detail?.message ?? (typeof detail === 'string' ? detail : null);
      alert(message || 'Failed to update workflow access');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (wcfg: string, on: boolean) => {
    const base = current ?? available.map((w) => w.wcfg);
    const next = on ? [...new Set([...base, wcfg])] : base.filter((k) => k !== wcfg);
    void save(next);
  };

  return (
    <Card title="Workflows">
      {isLoading && <p className="text-sm text-gray-400">Loading...</p>}
      {error && <p className="text-sm text-red-600">Failed to load workflow access</p>}
      {!isLoading && !error && (
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name={`workflow-access-${orgId}`}
                aria-label="Everything this environment admits"
                checked={!overridden}
                disabled={saving}
                onChange={() => void save(null)}
                className="mt-1"
              />
              <span>
                Everything this environment admits
                <span className="block text-xs text-gray-400">
                  No override — the firm follows the registry, as it does today
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name={`workflow-access-${orgId}`}
                aria-label="Only these"
                checked={overridden}
                disabled={saving}
                onChange={() => void save(available.map((w) => w.wcfg))}
                className="mt-1"
              />
              <span>
                Only these
                <span className="block text-xs text-gray-400">
                  An explicit set for this firm — narrows the environment, never widens it
                </span>
              </span>
            </label>
          </div>

          {available.length === 0 && (
            <p className="text-xs text-gray-400">
              This environment&apos;s registry admits no workflows.
            </p>
          )}

          {available.length > 0 && (
            <ul className="space-y-2 border-t border-gray-200 pt-3">
              {available.map((w) => (
                <li key={w.wcfg}>
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      aria-label={w.friendly_type}
                      checked={overridden && current!.includes(w.wcfg)}
                      disabled={saving || !overridden}
                      onChange={(e) => toggle(w.wcfg, e.target.checked)}
                      className="mt-1"
                    />
                    <span>
                      {w.friendly_type}
                      <span className="block text-xs text-gray-400 font-mono">{w.wcfg}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

export function EnterpriseDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const currentUser = getCurrentUser();
  const platformAdmin = isPlatformAdmin(currentUser);

  // org_admin may only ever view their own firm — never render a cross-org
  // fetch, redirect straight to their own detail (RFC-0212 A2). Computed
  // before the query below (not returned early) so hook call order stays
  // stable if the route params change without unmounting this component.
  const crossOrgRedirect =
    !platformAdmin && !!currentUser?.organizationId && orgId !== currentUser.organizationId;

  const { data, isLoading, error } = useQuery({
    queryKey: ['enterprise', orgId],
    queryFn: () => fetchEnterprise(orgId!),
    enabled: !!orgId && !crossOrgRedirect,
  });

  if (crossOrgRedirect) {
    return <Navigate to={`/enterprises/${currentUser!.organizationId}`} replace />;
  }

  if (isLoading) return <p className="text-gray-500">Loading enterprise...</p>;
  if (error) return <p className="text-red-600">Failed to load enterprise</p>;
  if (!data) return <p className="text-red-600">Enterprise not found</p>;

  const org = data.org || {};

  return (
    <div>
      {platformAdmin && (
        <Link to="/enterprises" className="text-sm text-blue-600 hover:underline flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> Back to enterprises
        </Link>
      )}

      <h1 className="text-xl font-bold mb-1">{org.name || 'Enterprise Detail'}</h1>
      <p className="text-xs text-gray-400 font-mono mb-6">{org.ext_id}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <EntitlementPanel orgId={orgId!} entitlement={data.entitlement} canEdit={platformAdmin} />
        <LoginPolicyPanel orgId={orgId!} />
        {/* Platform admin ONLY. The Workspace is an entitlement, so a firm's
            own org_admin must not grant it to themselves — the BFF refuses
            them (403) regardless, but rendering a control that always fails is
            worse than not rendering it. Login Policy above is different on
            purpose: a firm choosing its own sign-in method is theirs to make. */}
        {platformAdmin && <WorkspacePolicyPanel orgId={orgId!} />}
        {/* Platform admin ONLY, for the same reason as the Workspace above:
            which workflows a firm may run is an entitlement with a price on
            it, so a firm's own org_admin granting it to themselves is
            self-entitlement. The BFF refuses them regardless. */}
        {platformAdmin && <WorkflowAccessPanel orgId={orgId!} />}
      </div>

      <UsersPanel orgId={orgId!} />

      {/* Guest access (RFC-0510 W44). Platform admin ONLY, and for a sharper
          reason than the entitlement panels above: this grants somebody from
          ANOTHER organisation access to this firm's work. The firm's own
          org_admin does that in the product's Team page, over ent-api; the BFF
          refuses them here outright.

          ABSENT on an unactivated firm rather than disabled. Such a firm has no
          org at all yet — there is no id to grant into, no id to be a home
          organisation, and nothing the panel could truthfully show. Activate
          the firm first and the section appears. */}
      {platformAdmin && org.activated !== false && !!org.org_id && (
        <div className="mt-4">
          <GuestsPanel orgId={org.org_id} />
        </div>
      )}
    </div>
  );
}
